import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../lib/auth';
import AppHeader from './AppHeader';
import SetPassword from './SetPassword';
import { errorBox, noticeBox } from './AuthLayout';

const GOOGLE_ICON = (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

/** Supabase's errors from the Google round trip, in plain words. */
function friendly(message = '') {
  const m = message.toLowerCase();
  if (m.includes('manual linking')) return 'Linking Google accounts is not switched on yet. Please try again later.';
  if (m.includes('already') && (m.includes('linked') || m.includes('exists'))) {
    return 'That Google account is already used by another Carduzo account. Sign in with Google to use that account, or pick a different Google account.';
  }
  return message || 'Something went wrong. Please try again.';
}

/** /account — sign-in methods (link a Google account, any address) and password. */
export default function Account() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [identities, setIdentities] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.auth.getUserIdentities();
    if (err) setError(friendly(err.message));
    setIdentities(data?.identities || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back from Google: success lands on ?linked=google, failures come back as error_description
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const err = params.get('error_description') || hash.get('error_description');
    if (err) setError(friendly(err.replace(/\+/g, ' ')));
    else if (params.get('linked') === 'google') setNotice('Google account linked. You can now sign in with Google.');
    if (err || params.get('linked')) {
      setParams({}, { replace: true });
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
      supabase.auth.refreshSession().then(load);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const google = (identities || []).filter((i) => i.provider === 'google');
  const hasPassword = (identities || []).some((i) => i.provider === 'email');

  const link = async () => {
    setError(''); setNotice('');
    setBusy('link');
    const { error: err } = await supabase.auth.linkIdentity({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/account?linked=google`, queryParams: { prompt: 'select_account' } },
    });
    // On success the browser leaves for Google
    if (err) { setBusy(''); setError(friendly(err.message)); }
  };

  const unlink = async (identity) => {
    const label = identity.identity_data?.email || 'this Google account';
    if (!window.confirm(`Stop signing in with ${label}?`)) return;
    setError(''); setNotice('');
    setBusy(identity.identity_id);
    const { error: err } = await supabase.auth.unlinkIdentity(identity);
    setBusy('');
    if (err) return setError(friendly(err.message));
    setNotice(`${label} is no longer linked.`);
    await supabase.auth.refreshSession();
    load();
  };

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-[28px] leading-9 font-extrabold tracking-tight">Your account</h1>
          <p className="text-ink-muted mt-1">Signed in as <strong className="text-ink">{user?.email}</strong></p>
        </div>
        {error && <div className={errorBox} role="alert">{error}</div>}
        {notice && <div className={noticeBox} role="status">{notice}</div>}

        <section className="bg-white rounded-2xl border border-line p-5 sm:p-6 space-y-4">
          <div>
            <h2 className="font-semibold text-ink">Sign in with Google</h2>
            <p className="text-sm text-ink-muted mt-1">
              Link a Google account to sign in with one click. It can be any Google or Gmail address, even one that's different from {user?.email}.
            </p>
          </div>
          {identities === null ? <p className="text-sm text-ink-muted">Loading…</p> : (
            <>
              {google.length > 0 && (
                <ul className="divide-y divide-line border border-line rounded-xl">
                  {google.map((g) => (
                    <li key={g.identity_id} className="flex items-center gap-3 px-4 py-3">
                      {GOOGLE_ICON}
                      <span className="flex-1 min-w-0 truncate text-sm font-medium">{g.identity_data?.email || 'Google account'}</span>
                      {identities.length > 1 && (
                        <button type="button" onClick={() => unlink(g)} disabled={!!busy}
                          className="text-sm font-medium text-coral-ink hover:underline disabled:opacity-50">
                          {busy === g.identity_id ? 'Unlinking…' : 'Unlink'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={link} disabled={!!busy}
                className="h-11 px-4 rounded-xl border border-line-strong bg-white text-ink font-semibold inline-flex items-center gap-3 hover:bg-surface disabled:opacity-60">
                {GOOGLE_ICON}
                {busy === 'link' ? 'Opening Google…' : google.length ? 'Link another Google account' : 'Link a Google account'}
              </button>
              {google.length > 0 && identities.length === 1 && (
                <p className="text-xs text-ink-muted">Google is your only way to sign in, so it can't be unlinked. Set a password below first.</p>
              )}
            </>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-line p-5 sm:p-6 space-y-4">
          <div>
            <h2 className="font-semibold text-ink">{hasPassword ? 'Change password' : 'Set a password'}</h2>
            <p className="text-sm text-ink-muted mt-1">
              {hasPassword ? `For signing in with ${user?.email} and a password.` : `Add a password to also sign in with ${user?.email}.`}
            </p>
          </div>
          <SetPassword onDone={load} />
        </section>
      </main>
    </div>
  );
}
